import React from 'react';
import { Download, Laptop, Monitor, RotateCcw, MessageSquare } from 'lucide-react';

/**
 * Footage Pull 2.9, EDITORS (2026-09-16). An update notice, not a feature tour.
 *
 * Every Mac copy before 2.9 could not open a secure connection: it said the portal could not
 * be reached and downloaded nothing. Its own "a newer version is available" check goes through
 * that same connection, so the app can never tell a Mac editor to update. This notice can.
 *
 * The server cannot tell a Mac editor from a Windows one, so both are addressed, Mac first and
 * in red because only Mac editors have to act. The replace steps are the Editor Guide's own
 * ("When it says a newer version is available"), so the two cannot disagree.
 */

const Eyebrow = ({ children }) => (
  <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500 block">
    {children}
  </span>
);

export function FootagePull29Announcement({ onAcknowledge }) {
  return (
    <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-ug-raised border border-ug-border shadow-2xl"
         data-testid="whats-new-footage-pull-29">
      <div className="p-6 pb-5 border-b border-ug-border-subtle">
        <div className="flex items-center gap-2 mb-3">
          <span className="font-sans text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-ug-gold/15 text-ug-gold border border-ug-gold/25">Update</span>
          <Download className="w-3.5 h-3.5 text-ug-gold" strokeWidth={2} />
        </div>
        <h2 className="font-heading text-2xl text-zinc-100 mb-2">Footage Pull 2.9 fixes the Mac app</h2>
        <p className="text-[13px] text-zinc-100/75 leading-relaxed">
          On a Mac, Footage Pull could say &ldquo;The portal could not be reached&rdquo; and download nothing. That was a
          bug in the app, not your internet. Version 2.9, released September 16, fixes it.
        </p>
        <div className="mt-3 rounded-lg border border-ug-status-red/40 bg-ug-status-red/[0.06] px-3 py-2.5" data-testid="footage-pull-29-mac-note">
          {/* text-red-300, as the song notice: the red token measured 4.50:1 on this tinted box in dark. */}
          <p className="text-[12px] font-semibold text-red-300 leading-relaxed">
            On a Mac, download it again. The old app cannot tell you about this update, because it checks for updates over the same connection that was broken.
          </p>
        </div>
      </div>
      <div className="p-6 py-5 bg-ug-surface/30 space-y-3">
        <Eyebrow>What to do</Eyebrow>
        <div className="flex flex-col gap-2">
          {[[Laptop, 'On a Mac', 'Download 2.9 from the Editor Guide. Unzip it, drag Footage Pull into Applications and choose Replace. Your Mac asks you to allow it once more, the same way as the first time.'],
            [Monitor, 'On Windows', 'Nothing is broken. The app tells you at the start of a pull that a newer version is available; when convenient, delete the old Footage Pull folder and unzip 2.9 in its place.'],
            [RotateCcw, 'Nothing is lost', 'Your login and any half-finished downloads carry over. Open the project again and press Start.']].map(([Icon, t, d], i) => (
            <div key={i} className="flex items-start gap-2 rounded bg-ug-surface border border-ug-border-strong px-2.5 py-2">
              <Icon className="w-3.5 h-3.5 text-oxidized-teal shrink-0 mt-0.5" />
              <span className="text-[11px] text-zinc-100/75 leading-relaxed"><span className="text-zinc-100 font-medium">{t}.</span> {d}</span>
            </div>
          ))}
        </div>
        <div className="flex gap-3 pt-1">
          <MessageSquare className="shrink-0 w-4 h-4 text-slate-500 mt-0.5" strokeWidth={2} />
          <p className="text-[11px] text-slate-500 leading-relaxed">
            The download buttons are in the Editor Guide, under Getting Started, Workflow Steps.
          </p>
        </div>
      </div>
      <div className="p-6 pt-5 border-t border-ug-border-subtle bg-ug-surface/20">
        <button data-testid="whats-new-next" onClick={() => onAcknowledge && onAcknowledge()}
                className="tap-auto w-full py-2.5 rounded-lg bg-gold-leaf text-obsidian font-sans text-[11px] font-semibold uppercase tracking-wider">
          Got it
        </button>
      </div>
    </div>
  );
}

export default FootagePull29Announcement;
